import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { noop } from 'lodash';
import { usePathname } from 'next/navigation';
import { createContext, useContextSelector } from 'use-context-selector';
import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import type { ValidHref } from '@frameio/shared/routing';
import { isWorkspacesPageType } from '@frameio/shared/routing/pages/workspaces';
import {
  PRIMARY_NAV_COLLAPSED_WIDTH,
  PRIMARY_NAV_EXPANDED_WIDTH,
} from '../constants';

export type PrimaryNavState = 'collapsed' | 'expanded';

export type PrimaryNavContext = {
  state: PrimaryNavState;
  setState: Dispatch<SetStateAction<PrimaryNavState>>;
};

const PrimaryNavContext = createContext<PrimaryNavContext>({
  state: 'collapsed',
  setState: noop,
});

export const PrimaryNavContextProvider = ({ children }: PropsWithChildren) => {
  const [navState, setNavState] = useState<PrimaryNavState>('collapsed');
  const path = usePathname();

  const value = useMemo<PrimaryNavContext>(
    () => ({
      state: navState,
      setState: setNavState,
    }),
    [navState]
  );

  useEffect(() => {
    const isWorkspaces = isWorkspacesPageType(path as ValidHref);
    if (isWorkspaces) {
      setNavState('expanded');
    } else {
      setNavState('collapsed');
    }
  }, [path]);

  return (
    <PrimaryNavContext.Provider value={value}>
      {children}
    </PrimaryNavContext.Provider>
  );
};

export const usePrimaryNavState = () =>
  useContextSelector(PrimaryNavContext, ({ state }) => state);

export const useSetPrimaryNavState = () =>
  useContextSelector(PrimaryNavContext, ({ setState }) => setState);

export const useGetPrimaryNavBaseWidth = () => {
  const isGlobalNavEnabled = useFeatureFlagEnabled('v4.global_navigation');
  const state = usePrimaryNavState();

  return useMemo(() => {
    if (!isGlobalNavEnabled) {
      return PRIMARY_NAV_COLLAPSED_WIDTH;
    }
    switch (state) {
      case 'expanded':
        return PRIMARY_NAV_EXPANDED_WIDTH;
      default:
        return PRIMARY_NAV_COLLAPSED_WIDTH;
    }
  }, [state, isGlobalNavEnabled]);
};
